/**
 * A searchable MULTI-select that shows its choices as removable pills.
 *
 * The missing primitive. We had `Combobox` (searchable, single value) and two
 * bespoke chip inputs for emails and agents, but nothing generic — so every
 * multi-value control fell back to a native `<select multiple>`, which is a
 * scrolling box of highlighted rows that nobody can read at a glance.
 *
 * The reference's filter uses this shape everywhere (`fs-multiple-option`
 * pills, an "Enter or Select" input, a remove button per pill), and Kai's
 * verdict on the native version was blunt and correct: *"still looks crap
 * compared"*. The difference is not decoration — with pills you can see every
 * chosen value and drop one without touching the others, which a
 * multi-select's ctrl-click cannot do.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { NO_AUTOFILL } from './input';
import { cn } from '../../lib/utils';

export interface TokenOption {
  value: string;
  label: string;
}

export function TokenSelect({
  value,
  options,
  onChange,
  placeholder = 'Enter or select',
  /** Accept a typed value that is not in the list — right for tags, wrong for
   *  an id. Off by default: inventing an id silently matches nothing. */
  allowFree = false,
  className,
  ariaLabel,
}: {
  value: string[];
  options: TokenOption[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  allowFree?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  // URL state on purpose: a TYPEAHEAD BUFFER inside a picker, not view state.
  // The chosen values are the state that matters and they live in the caller's
  // condition; half-typed search text is not something to share or restore.
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * Close when the pointer OR the focus leaves — Step 208.
   *
   * `mousedown` alone was not enough. Kai, on the note composer's "Notify
   * to": the suggestion list stayed open over the editor while he typed the
   * note underneath it. Focus had moved by a route that involves no click on
   * the document — Tab, or a component taking focus itself, which is exactly
   * what a rich-text editor does when it mounts or is clicked through a
   * wrapper.
   *
   * `focusout` is the event that actually answers "is this control still the
   * one being used", and `relatedTarget` says where focus went: null means it
   * left the window entirely, which should not close a list somebody is about
   * to come back to.
   */
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onFocusOut = (e: FocusEvent) => {
      const to = e.relatedTarget as Node | null;
      if (!to) return; // focus left the window, not this control
      if (rootRef.current && !rootRef.current.contains(to)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const q = query.trim().toLowerCase();
  const available = options.filter(
    (o) => !value.includes(o.value) && (!q || o.label.toLowerCase().includes(q)),
  );

  const add = (v: string) => {
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setQuery('');
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1',
          'focus-within:ring-2 focus-within:ring-ring',
        )}
        onClick={() => setOpen(true)}
      >
        {value.map((v) => (
          <span
            key={v}
            className="inline-flex max-w-full items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs"
          >
            <span className="truncate">{labelFor(v)}</span>
            <button
              aria-label={`Remove ${labelFor(v)}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(value.filter((x) => x !== v));
              }}
              className="shrink-0 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          {...NO_AUTOFILL}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              // Enter takes the single obvious match, or a free value where
              // that is allowed — typing a whole tag and pressing Enter must
              // not silently do nothing.
              if (available.length === 1) add(available[0]!.value);
              else if (allowFree && query.trim()) add(query.trim());
            } else if (e.key === 'Backspace' && !query && value.length) {
              onChange(value.slice(0, -1));
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={value.length ? '' : placeholder}
          className="min-w-[6rem] flex-1 border-0 bg-transparent p-0.5 text-xs outline-none placeholder:text-muted-foreground"
        />
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </div>

      {open && (available.length > 0 || (allowFree && query.trim())) && (
        <div className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-card p-1 shadow-lg">
          {allowFree && query.trim() && !options.some((o) => o.label.toLowerCase() === q) && (
            <button
              onClick={() => add(query.trim())}
              className="flex w-full items-center gap-1 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
            >
              Use “{query.trim()}”
            </button>
          )}
          {available.map((o) => (
            <button
              key={o.value}
              onClick={() => add(o.value)}
              className="block w-full truncate rounded px-2 py-1.5 text-left text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
